<script setup>
    // Component
    import MainHeader from '@/components/HeaderComponent.vue';
    import Pagination from '@/components/PaginationComponent.vue';
    import PasswordInput from '@/components/PasswordInputComponent.vue';

    // Store
    import { useSettingStore } from '@/stores/settings/settingStore';
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Vue
    import { inject, onMounted, reactive, ref } from 'vue';
    import { useRouter } from 'vue-router';

    // commonProject
    import Utils from '@/commonProject/helpers/utils.js';
    import i18n from '@/common/i18n';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // ----------------------------
    // 定数
    // ----------------------------
    const router = useRouter();
    const settingStore = useSettingStore();
    const commonStore = useCommonStore();

    // ----------------------------
    // リアクティブ変数
    // ----------------------------
    const activeTab = ref('agreeTab');
    const securityRequest = reactive(settingStore.settingSecurity);
    const contactRequest = reactive(settingStore.settingContact);
    const companyPwd = ref('');
    const familyPwd = ref('');
    const securityPwdExpires = ref(['0', '1', '2', '3']);
    const currentPage = ref(1);
    const totalPages = ref(0);

    // 多要素認証の初期座標を設定する。初期値は「なし」
    const securityMfaFlag = ref('0');

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    // ----------------------------
    // 関数
    // ----------------------------
    // ご契約情報タブ押下時
    async function agreeTabClick() {
        activeTab.value = 'agreeTab';
        // 契約情報取得API
        const response = await settingStore.getAgreeAction(commonStore.API_URL);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    // セキュリティ設定タブ押下時
    async function securityTabClick() {
        activeTab.value = 'securityTab';
        // セキュリティ設定取得API
        const response = await settingStore.getSecurityAction(commonStore.API_URL);
        if (response.success) {
            securityRequest.SecurityPwdPolicy = Utils.changeNullValueToEmpty(settingStore.settingSecurity.SecurityPwdPolicy);
            securityRequest.SecurityPwdExpire = Utils.changeNullValueToEmpty(settingStore.settingSecurity.SecurityPwdExpire);
            securityRequest.SecurityMfaFlag = Utils.isNullEmpty(settingStore.settingSecurity.SecurityMfaFlag) ? false : settingStore.settingSecurity.SecurityMfaFlag;
            securityRequest.SecurityIpList = Utils.changeNullValueToEmpty(settingStore.settingSecurity.SecurityIpList);
            securityRequest.SecurityMailSpeed = Utils.isNullEmpty(settingStore.settingSecurity.SecurityMailSpeed) ? '' : String(settingStore.settingSecurity.SecurityMailSpeed);

            // 多要素認証のラジオボタンの座標を、APIで取得した結果に応じて再設定する。
            securityMfaFlag.value = securityRequest.SecurityMfaFlag === true ? '1' : '0';
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    // 連絡先設定タブ押下時
    async function contactTabClick() {
        activeTab.value = 'contactTab';
        // 連絡先設定取得API
        const response = await settingStore.getContactAction(commonStore.API_URL);
        if (response.success) {
            contactRequest.EmergencyDayDepa = Utils.changeNullValueToEmpty(settingStore.settingContact.EmergencyDayDepa);
            contactRequest.EmergencyDayPerson = Utils.changeNullValueToEmpty(settingStore.settingContact.EmergencyDayPerson);
            contactRequest.EmergencyDayTel = Utils.changeNullValueToEmpty(settingStore.settingContact.EmergencyDayTel);
            contactRequest.EmergencyDayMail = Utils.changeNullValueToEmpty(settingStore.settingContact.EmergencyDayMail);
            contactRequest.EmergencyNightDepa = Utils.changeNullValueToEmpty(settingStore.settingContact.EmergencyNightDepa);
            contactRequest.EmergencyNightPerson = Utils.changeNullValueToEmpty(settingStore.settingContact.EmergencyNightPerson);
            contactRequest.EmergencyNightTel = Utils.changeNullValueToEmpty(settingStore.settingContact.EmergencyNightTel);
            contactRequest.EmergencyNightMail = Utils.changeNullValueToEmpty(settingStore.settingContact.EmergencyNightMail);
            contactRequest.EmergencyHolidayDepa = Utils.changeNullValueToEmpty(settingStore.settingContact.EmergencyHolidayDepa);
            contactRequest.EmergencyHolidayPerson = Utils.changeNullValueToEmpty(settingStore.settingContact.EmergencyHolidayPerson);
            contactRequest.EmergencyHolidayTel = Utils.changeNullValueToEmpty(settingStore.settingContact.EmergencyHolidayTel);
            contactRequest.EmergencyHolidayMail = Utils.changeNullValueToEmpty(settingStore.settingContact.EmergencyHolidayMail);
            contactRequest.CounterPowerHaraDepa = Utils.changeNullValueToEmpty(settingStore.settingContact.CounterPowerHaraDepa);
            contactRequest.CounterPowerHaraPerson = Utils.changeNullValueToEmpty(settingStore.settingContact.CounterPowerHaraPerson);
            contactRequest.CounterPowerHaraTel = Utils.changeNullValueToEmpty(settingStore.settingContact.CounterPowerHaraTel);
            contactRequest.CounterPowerHaraMail = Utils.changeNullValueToEmpty(settingStore.settingContact.CounterPowerHaraMail);
            contactRequest.CounterSexualHaraDepa = Utils.changeNullValueToEmpty(settingStore.settingContact.CounterSexualHaraDepa);
            contactRequest.CounterSexualHaraPerson = Utils.changeNullValueToEmpty(settingStore.settingContact.CounterSexualHaraPerson);
            contactRequest.CounterSexualHaraTel = Utils.changeNullValueToEmpty(settingStore.settingContact.CounterSexualHaraTel);
            contactRequest.CounterSexualHaraMail = Utils.changeNullValueToEmpty(settingStore.settingContact.CounterSexualHaraMail);
            contactRequest.CounterComplianceDepa = Utils.changeNullValueToEmpty(settingStore.settingContact.CounterComplianceDepa);
            contactRequest.CounterCompliancePerson = Utils.changeNullValueToEmpty(settingStore.settingContact.CounterCompliancePerson);
            contactRequest.CounterComplianceTel = Utils.changeNullValueToEmpty(settingStore.settingContact.CounterComplianceTel);
            contactRequest.CounterComplianceMail = Utils.changeNullValueToEmpty(settingStore.settingContact.CounterComplianceMail);
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    // 企業/家族パスワード設定タブ押下時
    async function companyPasswordTabClick() {
        activeTab.value = 'passwordTab';
        // 企業/家族パスワード設定API
        const response = await settingStore.getPasswordAction(commonStore.API_URL);
        if (response.success) {
            companyPwd.value = Utils.changeNullValueToEmpty(settingStore.settingPassword.CompanyPwd);
            familyPwd.value = Utils.changeNullValueToEmpty(settingStore.settingPassword.FamilyPwd);
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    // 再委託先情報タブ押下時
    async function recommitmentTabClick() {
        activeTab.value = 'recommitmentTab';
        // 再委託先情報取得API
        const response = await settingStore.getRecommitmentAction(commonStore.API_URL);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    // ログ情報タブ押下時
    async function logTabClick() {
        activeTab.value = 'logTab';
        currentPage.value = 1;
        getLogList();
    }

    async function getLogList() {
        const maxPage = 50;
        // ログ一覧情報取得API
        const logListRequest = {
            PageCond: {
                RowPosition: maxPage * (currentPage.value - 1) + 1,
                RowAmount: maxPage,
                RowCount: true,
            },
        };
        const response = await settingStore.getLogAction(commonStore.API_URL, logListRequest);
        if (response.success) {
            totalPages.value = settingStore.pageInfo;
            handleOnloadPagination(totalPages.value);
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    async function handlePageChange(newPage) {
        currentPage.value = newPage;
        await getLogList();
    }
    const pagination = ref();
    /**
     * ページングの初期処理を実行する。
     * 取得したAPIのデータ数を引き渡す。
     */
    function handleOnloadPagination(RowResult) {
        pagination.value.onload(RowResult);
    }

    // 戻るボタン押下時
    function onBack() {
        router.back();
    }

    // セキュリティ設定登録ボタン押下時
    async function securityCreate() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        } else {
            // ボタン連打ガード制御
            disabledStore.buttonTemporaryDisabled();

            // セキュリティ設定更新APIのリクエストボディを生成する。
            const requestBody = {
                SecurityPwdPolicy: securityRequest.SecurityPwdPolicy,
                SecurityPwdExpire: securityRequest.SecurityPwdExpire,
                SecurityMfaFlag: securityMfaFlag.value === '1' ? true : false,
                SecurityIpList: securityRequest.SecurityIpList,
                SecurityMailSpeed: Utils.changeValueToNumberOrNull(securityRequest.SecurityMailSpeed),
            };
            // セキュリティ設定更新API
            const response = await settingStore.getSecurityUpdateAction(commonStore.API_URL, requestBody);
            if (response.success) {
                messageDialog('success', i18n.global.t('common.message.updateSuccess'));

                // 画面を更新するために、企業情報取得APIをコールする。
                // タブ部分のアラートマークなどを更新する。
                await callCompanyApiActionFunction();
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
            }
        }
    }

    // 連絡先設定登録ボタン押下時
    async function contactCreate() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        } else {
            // ボタン連打ガード制御
            disabledStore.buttonTemporaryDisabled();

            // APIのレスポンスで取得した構造から、リクエストに不要な情報を除外するために、JSON構造をコピーする。
            let requestBody = JSON.parse(JSON.stringify(contactRequest));

            // APIのレスポンスで取得した構造から、リクエストに不要な情報を除外する。
            delete requestBody.ApiRes;

            // 連絡先設定更新API
            const response = await settingStore.getContactUpdateAction(commonStore.API_URL, requestBody);
            if (response.success) {
                messageDialog('success', i18n.global.t('common.message.updateSuccess'));

                // 画面を更新するために、企業情報取得APIをコールする。
                // タブ部分のアラートマークなどを更新する。
                await callCompanyApiActionFunction();
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
            }
        }
    }

    // 企業/家族パスワード設定登録ボタン押下時
    async function recommitmentCreate() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        } else {
            // ボタン連打ガード制御
            disabledStore.buttonTemporaryDisabled();

            // 企業パスワード設定更新APIのリクエストボディを生成する。
            let requestBody = {
                CompanyPwd: companyPwd.value,
                FamilyPwd: familyPwd.value,
            };

            // 本企業がタフネス連携企業かどうか判定する。
            // タスネス連携企業ではない場合のみ、家族パスワードが更新対象になる。
            // if (!commonStore.companyInfo.TouLinkFlag) {
            //    requestBody['FamilyPwd'] = familyPwd.value;
            // }

            // 企業/家族パスワード設定更新API
            const response = await settingStore.getPasswordUpdateAction(commonStore.API_URL, requestBody);
            if (response.success) {
                messageDialog('success', i18n.global.t('common.message.updateSuccess'));

                // 画面を更新するために、企業情報取得APIをコールする。
                // タブ部分のアラートマークなどを更新する。
                await callCompanyApiActionFunction();
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
            }
        }
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // 選択されているタブに応じて、入力内容のチェックを行う。
        switch (activeTab.value) {
            case 'securityTab':
                // パスワード変更催促までの期間
                if (Utils.isNullEmpty(securityRequest.SecurityPwdExpire)) {
                    message += i18n.global.t('settings.message.message1') + '\n';
                }
                // 多要素認証
                if (Utils.isNullEmpty(securityMfaFlag)) {
                    message += i18n.global.t('settings.message.message2') + '\n';
                }
                // 管理者サイト許可IPアドレスが入力されている場合のみ内容をチェックする。
                if (!Utils.isNullEmpty(securityRequest.SecurityIpList)) {
                    // 文字数を超過している場合。
                    if (Utils.checkLengthOfLongString(securityRequest.SecurityIpList)) {
                        message += i18n.global.t('settings.message.message61') + '\n';
                    }
                    // 不正の場合
                    // 「スペース」「カンマ」「改行」で分割
                    const ips = securityRequest.SecurityIpList.split(/[\s,]+/);
                    if (ips.some((ip) => !Utils.isMatchIpAddress(ip))) {
                        message += i18n.global.t('settings.message.message62') + '\n';
                    }
                }
                // メール送信間隔が入力されている場合のみ内容をチェックする。
                if (!Utils.isNullEmpty(securityRequest.SecurityMailSpeed)) {
                    // 数字のみ許容する。
                    if (Utils.isNumberNaN(securityRequest.SecurityMailSpeed)) {
                        message += i18n.global.t('settings.message.message63') + '\n';
                    }
                    // int32の範囲超過を判定
                    if (Utils.checkLengthOfDisplayOrderNumber(securityRequest.SecurityMailSpeed)) {
                        message += i18n.global.t('settings.message.message64') + '\n';
                    }
                }
                // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
                if (!Utils.isNullEmpty(message)) {
                    // エラーメッセージが存在する場合は、画面に表示する。
                    messageDialog('error', message);
                    return true;
                }

                return false;
            case 'contactTab':
                // 日中部署
                if (Utils.isNullEmpty(contactRequest.EmergencyDayDepa)) {
                    message += i18n.global.t('settings.message.message7') + '\n';
                } else {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.EmergencyDayDepa)) {
                        message += i18n.global.t('settings.message.message8') + '\n';
                    }
                }
                // 日中氏名
                if (Utils.isNullEmpty(contactRequest.EmergencyDayPerson)) {
                    message += i18n.global.t('settings.message.message9') + '\n';
                } else {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.EmergencyDayPerson)) {
                        message += i18n.global.t('settings.message.message10') + '\n';
                    }
                }
                // 日中電話番号
                if (Utils.isNullEmpty(contactRequest.EmergencyDayTel)) {
                    message += i18n.global.t('settings.message.message11') + '\n';
                } else {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.EmergencyDayTel)) {
                        message += i18n.global.t('settings.message.message12') + '\n';
                    }
                }
                // 日中メール
                if (Utils.isNullEmpty(contactRequest.EmergencyDayMail)) {
                    message += i18n.global.t('settings.message.message14') + '\n';
                } else {
                    if (Utils.checkLengthOfSettingMailAddressString(contactRequest.EmergencyDayMail)) {
                        message += i18n.global.t('settings.message.message15') + '\n';
                    }
                }
                // 夜間部署
                if (Utils.isNullEmpty(contactRequest.EmergencyNightDepa)) {
                    message += i18n.global.t('settings.message.message17') + '\n';
                } else {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.EmergencyNightDepa)) {
                        message += i18n.global.t('settings.message.message18') + '\n';
                    }
                }
                //  夜間氏名
                if (Utils.isNullEmpty(contactRequest.EmergencyNightPerson)) {
                    message += i18n.global.t('settings.message.message19') + '\n';
                } else {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.EmergencyNightPerson)) {
                        message += i18n.global.t('settings.message.message20') + '\n';
                    }
                }
                // 夜間電話番号
                if (Utils.isNullEmpty(contactRequest.EmergencyNightTel)) {
                    message += i18n.global.t('settings.message.message21') + '\n';
                } else {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.EmergencyNightTel)) {
                        message += i18n.global.t('settings.message.message22') + '\n';
                    }
                }
                // 夜間メール
                if (Utils.isNullEmpty(contactRequest.EmergencyNightMail)) {
                    message += i18n.global.t('settings.message.message24') + '\n';
                } else {
                    if (Utils.checkLengthOfSettingMailAddressString(contactRequest.EmergencyNightMail)) {
                        message += i18n.global.t('settings.message.message25') + '\n';
                    }
                }
                // 土日祝日部署
                if (Utils.isNullEmpty(contactRequest.EmergencyHolidayDepa)) {
                    message += i18n.global.t('settings.message.message27') + '\n';
                } else {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.EmergencyHolidayDepa)) {
                        message += i18n.global.t('settings.message.message28') + '\n';
                    }
                }
                //  土日祝日氏名
                if (Utils.isNullEmpty(contactRequest.EmergencyHolidayPerson)) {
                    message += i18n.global.t('settings.message.message29') + '\n';
                } else {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.EmergencyHolidayPerson)) {
                        message += i18n.global.t('settings.message.message30') + '\n';
                    }
                }
                // 土日祝日電話番号
                if (Utils.isNullEmpty(contactRequest.EmergencyHolidayTel)) {
                    message += i18n.global.t('settings.message.message31') + '\n';
                } else {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.EmergencyHolidayTel)) {
                        message += i18n.global.t('settings.message.message32') + '\n';
                    }
                }
                // 土日祝日メール
                if (Utils.isNullEmpty(contactRequest.EmergencyHolidayMail)) {
                    message += i18n.global.t('settings.message.message34') + '\n';
                } else {
                    if (Utils.checkLengthOfSettingMailAddressString(contactRequest.EmergencyHolidayMail)) {
                        message += i18n.global.t('settings.message.message35') + '\n';
                    }
                }
                // パワハラ部署
                if (!Utils.isNullEmpty(contactRequest.CounterPowerHaraDepa)) {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.CounterPowerHaraDepa)) {
                        message += i18n.global.t('settings.message.message37') + '\n';
                    }
                }
                // パワハラ氏名
                if (!Utils.isNullEmpty(contactRequest.CounterPowerHaraPerson)) {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.CounterPowerHaraPerson)) {
                        message += i18n.global.t('settings.message.message38') + '\n';
                    }
                }
                // パワハラ電話番号
                if (!Utils.isNullEmpty(contactRequest.CounterPowerHaraTel)) {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.CounterPowerHaraTel)) {
                        message += i18n.global.t('settings.message.message39') + '\n';
                    }
                }
                // パワハラメール
                if (!Utils.isNullEmpty(contactRequest.CounterPowerHaraMail)) {
                    if (Utils.checkLengthOfSettingMailAddressString(contactRequest.CounterPowerHaraMail)) {
                        message += i18n.global.t('settings.message.message41') + '\n';
                    }
                }
                // セクハラ部署
                if (!Utils.isNullEmpty(contactRequest.CounterSexualHaraDepa)) {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.CounterSexualHaraDepa)) {
                        message += i18n.global.t('settings.message.message43') + '\n';
                    }
                }
                // セクハラ氏名
                if (!Utils.isNullEmpty(contactRequest.CounterSexualHaraPerson)) {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.CounterSexualHaraPerson)) {
                        message += i18n.global.t('settings.message.message44') + '\n';
                    }
                }
                // セクハラ電話番号
                if (!Utils.isNullEmpty(contactRequest.CounterSexualHaraTel)) {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.CounterSexualHaraTel)) {
                        message += i18n.global.t('settings.message.message45') + '\n';
                    }
                }
                // セクハラメール
                if (!Utils.isNullEmpty(contactRequest.CounterSexualHaraMail)) {
                    if (Utils.checkLengthOfSettingMailAddressString(contactRequest.CounterSexualHaraMail)) {
                        message += i18n.global.t('settings.message.message47') + '\n';
                    }
                }
                // コンプラ部署
                if (!Utils.isNullEmpty(contactRequest.CounterComplianceDepa)) {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.CounterComplianceDepa)) {
                        message += i18n.global.t('settings.message.message49') + '\n';
                    }
                }
                // コンプラ氏名
                if (!Utils.isNullEmpty(contactRequest.CounterCompliancePerson)) {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.CounterCompliancePerson)) {
                        message += i18n.global.t('settings.message.message50') + '\n';
                    }
                }
                // コンプラ電話番号
                if (!Utils.isNullEmpty(contactRequest.CounterComplianceTel)) {
                    if (Utils.checkLengthOfSettingCurrentString(contactRequest.CounterComplianceTel)) {
                        message += i18n.global.t('settings.message.message51') + '\n';
                    }
                }
                // コンプラメール
                if (!Utils.isNullEmpty(contactRequest.CounterComplianceMail)) {
                    if (Utils.checkLengthOfSettingMailAddressString(contactRequest.CounterComplianceMail)) {
                        message += i18n.global.t('settings.message.message53') + '\n';
                    }
                }
                // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
                if (!Utils.isNullEmpty(message)) {
                    // エラーメッセージが存在する場合は、画面に表示する。
                    messageDialog('error', message);
                    return true;
                }

                return false;
            case 'passwordTab':
                // 企業パスワードの入力チェック。
                if (Utils.isNullEmpty(companyPwd.value)) {
                    message += i18n.global.t('settings.message.message55') + '\n';
                } else {
                    if (Utils.checkLengthOfPassword(companyPwd.value)) {
                        message += i18n.global.t('settings.message.message56') + '\n';
                    }
                    if (!Utils.isMatchPatternAlphabetNumberMix(companyPwd.value)) {
                        message += i18n.global.t('settings.message.message57') + '\n';
                    }
                }
                // 家族パスワードの入力チェック。
                // 本企業がタフネス連携企業かどうか判定する。
                // タスネス連携企業の場合は、家族パスワードは変更不可。
                if (!commonStore.companyInfo.TouLinkFlag) {
                    // タスネス連携企業では無いので、家族パスワードの入力情報をチェックする。
                    if (Utils.isNullEmpty(familyPwd.value)) {
                        message += i18n.global.t('settings.message.message58') + '\n';
                    } else {
                        if (Utils.checkLengthOfPassword(familyPwd.value)) {
                            message += i18n.global.t('settings.message.message59') + '\n';
                        }
                        if (!Utils.isMatchPatternAlphabetNumberMix(familyPwd.value)) {
                            message += i18n.global.t('settings.message.message60') + '\n';
                        }
                    }
                }

                // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
                if (!Utils.isNullEmpty(message)) {
                    // エラーメッセージが存在する場合は、画面に表示する。
                    messageDialog('error', message);
                    return true;
                }
                return false;
            default:
                break;
        }
    }

    /**
     * 企業情報取得APIをコールする。
     *
     */
    async function callCompanyApiActionFunction() {
        // 企業情報取得API
        const response1 = await commonStore.getCompanyAction(commonStore.API_URL);
        if (!response1.success) {
            messageDialog('error', response1.errorMessage);
            if (response1.redirect) {
                location.href = response1.redirectUrl;
            }
        }
    }

    /**
     * 企業情報取得APIをコールする。
     *
     */
    async function callAgreeApiActionFunction() {
        // 契約情報取得API
        const response2 = await settingStore.getAgreeAction(commonStore.API_URL);
        if (!response2.success) {
            messageDialog('error', response2.errorMessage);
            if (response2.redirect) {
                location.href = response2.redirectUrl;
            }
        }
    }

    /**
     * 本画面を初期化する。
     * 全ての情報を初期化する。
     */
    function reset() {
        // Storeを初期化する。
        commonStore.companyInfo = {};
        settingStore.resetAgree();
    }

    // ----------------------------
    // ライフサイクル
    // ----------------------------
    onMounted(async () => {
        // 初期化する。
        reset();

        // 本画面でコールするAPIのドメインをStoreに格納した情報から取得する。
        await commonStore.getDomain();

        // ***** 画面初期表示用 ***** //

        // 企業情報取得APIをコールする。
        await callCompanyApiActionFunction();

        // 契約情報取得APIをコールする。
        await callAgreeApiActionFunction();

        /*
        // 企業情報取得API
        const response1 = await commonStore.getCompanyAction(commonStore.API_URL);
        if (!response1.success) {
            messageDialog('error', response1.errorMessage);
            if (response1.redirect) {
                location.href = response1.redirectUrl;
            }
        }
        // 契約情報取得API
        const response2 = await settingStore.getAgreeAction(commonStore.API_URL);
        if (!response2.success) {
            messageDialog('error', response2.errorMessage);
            if (response2.redirect) {
                location.href = response2.redirectUrl;
            }
        }
        */
    });
</script>
<style scoped>
    .planService {
        width: 100%;
        height: 30px;
        position: relative;
        display: block;
        line-height: 30px;
    }
</style>

<template>
    <section id="main">
        <MainHeader />
        <div id="contents">
            <div class="whiteBox wide">
                <h3 class="textCenter">
                    <span>{{ $t('settings.parts.pageTitle1') }}</span>
                </h3>
                <section class="whiteBoxContents">
                    <ul class="tab">
                        <li>
                            <button type="button" @click="agreeTabClick" :class="activeTab === 'agreeTab' ? 'selected' : ''">{{ $t('settings.parts.tabButton1') }}</button>
                        </li>
                        <li :class="{ emphasis: commonStore.companyInfo.AlertStgSec === true }">
                            <button type="button" @click="securityTabClick" :class="activeTab === 'securityTab' ? 'selected' : ''">{{ $t('settings.parts.tabButton2') }}</button>
                        </li>
                        <li :class="{ emphasis: commonStore.companyInfo.AlertStgCtt === true }">
                            <button type="button" @click="contactTabClick" :class="activeTab === 'contactTab' ? 'selected' : ''">{{ $t('settings.parts.tabButton3') }}</button>
                        </li>
                        <li :class="{ emphasis: commonStore.companyInfo.AlertStgPwd === true }">
                            <button type="button" @click="companyPasswordTabClick" :class="activeTab === 'passwordTab' ? 'selected' : ''">{{ $t('settings.parts.tabButton4') }}</button>
                        </li>
                        <li v-show="commonStore.companyInfo.SubcontractFlag">
                            <button type="button" @click="recommitmentTabClick" :class="activeTab === 'recommitmentTab' ? 'selected' : ''">{{ $t('settings.parts.tabButton5') }}</button>
                        </li>
                        <li>
                            <button type="button" @click="logTabClick" :class="activeTab === 'logTab' ? 'selected' : ''">{{ $t('settings.parts.tabButton6') }}</button>
                        </li>
                    </ul>
                    <article class="innerTabBox" v-show="activeTab === 'agreeTab'">
                        <!--契約情報-->
                        <div class="searchFilter" style="margin-bottom: 10px">
                            <ul>
                                <li>
                                    <dl class="dot">
                                        <dt>
                                            <!--企業名-->
                                            <ul class="formLabel">
                                                <li>
                                                    <label>{{ $t('settings.parts.dt1') }}</label>
                                                </li>
                                            </ul>
                                        </dt>
                                        <dd style="position: relative; display: flex">
                                            <ul class="formInput">
                                                <li>
                                                    <span>{{ commonStore.companyInfo.CompanyNm }}</span>
                                                </li>
                                            </ul>
                                        </dd>
                                    </dl>
                                </li>
                            </ul>
                            <ul>
                                <li>
                                    <dl class="dot">
                                        <dt>
                                            <!--登録人数-->
                                            <ul class="formLabel">
                                                <li>
                                                    <label>{{ $t('settings.parts.dl1') }}</label>
                                                </li>
                                            </ul>
                                        </dt>
                                        <dd>
                                            <ul class="formInput">
                                                <li>
                                                    <!--人事登録者数-->
                                                    <span>{{ $t('settings.parts.dt2') }}</span>
                                                </li>
                                                <li>
                                                    <span>{{ settingStore.userNumNmg + $t('settings.parts.tableBody4') }}</span>
                                                </li>
                                            </ul>
                                            <ul class="formInput" style="margin-left: 40px">
                                                <li>
                                                    <!--自己登録者数(従業員)-->
                                                    <label>{{ $t('settings.parts.dt3') }}</label>
                                                </li>
                                                <li>
                                                    <span>{{ settingStore.userNumSlfEmp + $t('settings.parts.tableBody4') }}</span>
                                                </li>
                                            </ul>
                                            <ul class="formInput" style="margin-left: 40px">
                                                <li>
                                                    <!--自己登録者数(家族)-->
                                                    <label>{{ $t('settings.parts.dt4') }}</label>
                                                </li>
                                                <li>
                                                    <span>{{ settingStore.userNumSlfFml + $t('settings.parts.tableBody4') }}</span>
                                                </li>
                                            </ul>
                                        </dd>
                                    </dl>
                                </li>
                            </ul>
                        </div>
                        <div class="searchFilter" style="margin-bottom: 0px">
                            <ul>
                                <li style="width: 100%">
                                    <dl class="dot">
                                        <dt>
                                            <!--契約-->
                                            <ul class="formLabel">
                                                <li>
                                                    <label>{{ $t('settings.parts.dt19') }}</label>
                                                </li>
                                            </ul>
                                        </dt>
                                    </dl>
                                </li>
                            </ul>
                        </div>
                        <article class="innerTable setting" style="height: 100%; margin-top: unset">
                            <table class="table data noSelection">
                                <thead>
                                    <tr>
                                        <th class="mediumWidth">
                                            <!--プラン-->
                                            <span>{{ $t('settings.parts.tableHeader1') }}</span>
                                        </th>
                                        <th class="largeWidth">
                                            <!--期間-->
                                            <span>{{ $t('settings.parts.tableHeader2') }}</span>
                                        </th>
                                        <th class="smallWidth">
                                            <!--自動更新の有無-->
                                            <span>{{ $t('settings.parts.tableHeader3') }}</span>
                                        </th>
                                        <th class="smallWidth">
                                            <!--自動更新の契約期間-->
                                            <span>{{ $t('settings.parts.tableHeader4') }}</span>
                                        </th>
                                        <th class="smallWidth">
                                            <!--契約人数-->
                                            <span>{{ $t('settings.parts.tableHeader5') }}</span>
                                        </th>
                                        <th class="smallWidth">
                                            <!--契約件数-->
                                            <span>{{ $t('settings.parts.tableHeader6') }}</span>
                                        </th>
                                        <th class="smallWidth">
                                            <!--利用者への契約割当-->
                                            <span>{{ $t('settings.parts.tableHeader7') }}</span>
                                        </th>
                                        <th class="smallWidth">
                                            <!--割当人数-->
                                            <span>{{ $t('settings.parts.tableHeader8') }}</span>
                                        </th>
                                        <th class="mediumWidth">
                                            <!--有効割当人数-->
                                            <span>{{ $t('settings.parts.tableHeader9') }}</span>
                                        </th>
                                        <th class="largeWidth">
                                            <!--サービス-->
                                            <span>{{ $t('settings.parts.tableHeader10') }}</span>
                                        </th>
                                        <th class="largeWidth">
                                            <!--利用件数-->
                                            <span>{{ $t('settings.parts.tableHeader11') }}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(list, index) in settingStore.aggreList" :key="index" class="combine">
                                        <template v-if="list.UsedServices.length === 0">
                                            <!--割当サービス無し-->
                                            <td>
                                                <!--契約プラン-->
                                                <span class="planService">{{ list.ContractPlanNm }}</span>
                                            </td>
                                            <td>
                                                <!--期間-->
                                                <span class="planService">{{ list.ContractDateS + ' - ' + list.ContractDateE }}</span>
                                            </td>
                                            <td>
                                                <!--あり/なし-->
                                                <span class="planService">{{ list.ContractAutoContFlag === true ? $t('settings.parts.tableBody1') : $t('settings.parts.tableBody2') }}</span>
                                            </td>
                                            <td>
                                                <!--計画期間カ月-->
                                                <span class="planService">{{ Utils.isNullEmpty(list.ContractAutoContMonth) ? '' : +list.ContractAutoContMonth + $t('settings.parts.tableBody3') }}</span>
                                            </td>
                                            <td>
                                                <!--契約人数-->
                                                <span class="planService">{{ Utils.isNullEmpty(list.ContractClientNum) ? '' : +list.ContractClientNum + $t('settings.parts.tableBody4') }}</span>
                                            </td>
                                            <td>
                                                <!--契約件数-->
                                                <span class="planService">{{ Utils.isNullEmpty(list.ContractSessionNum) ? '' : +list.ContractSessionNum + $t('settings.parts.tableBody5') }}</span>
                                            </td>
                                            <td>
                                                <template v-if="list.ContractApplyManual && !list.ContractApplyAuto">
                                                    <!--個別-->
                                                    <span class="planService">{{ $t('settings.parts.dt5') }}</span>
                                                </template>
                                                <template v-else-if="!list.ContractApplyManual && list.ContractApplyAuto">
                                                    <!--自動-->
                                                    <span class="planService">{{ $t('settings.parts.dt6') }}</span>
                                                </template>
                                                <template v-else-if="list.ContractApplyManual && list.ContractApplyAuto">
                                                    <!--個別・自動-->
                                                    <span class="planService">{{ $t('settings.parts.dt7') }}</span>
                                                </template>
                                                <template v-else>
                                                    <span></span>
                                                </template>
                                            </td>
                                            <td>
                                                <!--割当人数-->
                                                <span class="planService">{{ list.AllocationNum + $t('settings.parts.tableBody4') }}</span>
                                            </td>
                                            <td>
                                                <!--有効割当人数-->
                                                <span class="planService">{{ list.AllocationNumValid + $t('settings.parts.tableBody4') }}</span>
                                            </td>
                                            <!-- サービス・利用件数 -->
                                            <td colspan="2">
                                                <ul>
                                                    <li>
                                                        <span class="planService"></span>
                                                    </li>
                                                    <li>
                                                        <span class="planService">{{ '0' + $t('settings.parts.tableBody5') }}</span>
                                                    </li>
                                                </ul>
                                            </td>
                                        </template>
                                        <template v-else>
                                            <!--割当サービスあり-->
                                            <td>
                                                <!--契約プラン-->
                                                <span class="planService" v-for="(servicesList, servicesIndex) in list.UsedServices" :key="servicesIndex">{{ servicesIndex == 0 ? list.ContractPlanNm : '' }}</span>
                                            </td>
                                            <td>
                                                <!--期間-->
                                                <span class="planService" v-for="(servicesList, servicesIndex) in list.UsedServices" :key="servicesIndex">{{ servicesIndex == 0 ? list.ContractDateS + ' - ' + list.ContractDateE : '' }}</span>
                                            </td>
                                            <td>
                                                <!--あり/なし-->
                                                <span class="planService" v-for="(servicesList, servicesIndex) in list.UsedServices" :key="servicesIndex">{{ servicesIndex == 0 ? (list.ContractAutoContFlag === true ? $t('settings.parts.tableBody1') : $t('settings.parts.tableBody2')) : '' }}</span>
                                            </td>
                                            <td>
                                                <!--計画期間カ月-->
                                                <span class="planService" v-for="(servicesList, servicesIndex) in list.UsedServices" :key="servicesIndex">{{ servicesIndex == 0 ? (Utils.isNull(list.ContractAutoContMonth) ? '' : list.ContractAutoContMonth + $t('settings.parts.tableBody3')) : '' }}</span>
                                            </td>
                                            <td>
                                                <!--契約人数-->
                                                <span class="planService" v-for="(servicesList, servicesIndex) in list.UsedServices" :key="servicesIndex">{{ servicesIndex == 0 ? (Utils.isNull(list.ContractClientNum) ? '' : list.ContractClientNum + $t('settings.parts.tableBody4')) : '' }}</span>
                                            </td>
                                            <td>
                                                <!--契約件数-->
                                                <span class="planService" v-for="(servicesList, servicesIndex) in list.UsedServices" :key="servicesIndex">{{ servicesIndex == 0 ? (Utils.isNull(list.ContractSessionNum) ? '' : list.ContractSessionNum + $t('settings.parts.tableBody5')) : '' }}</span>
                                            </td>
                                            <td>
                                                <template v-if="list.ContractApplyManual && !list.ContractApplyAuto">
                                                    <!--個別-->
                                                    <span class="planService" v-for="(servicesList, servicesIndex) in list.UsedServices" :key="servicesIndex">{{ servicesIndex == 0 ? $t('settings.parts.dt5') : '' }}</span>
                                                </template>
                                                <template v-else-if="!list.ContractApplyManual && list.ContractApplyAuto">
                                                    <!--自動-->
                                                    <span class="planService" v-for="(servicesList, servicesIndex) in list.UsedServices" :key="servicesIndex">{{ servicesIndex == 0 ? $t('settings.parts.dt6') : '' }}</span>
                                                </template>
                                                <template v-else-if="list.ContractApplyManual && list.ContractApplyAuto">
                                                    <!--個別・自動-->
                                                    <span class="planService" v-for="(servicesList, servicesIndex) in list.UsedServices" :key="servicesIndex">{{ servicesIndex == 0 ? $t('settings.parts.dt7') : '' }}</span>
                                                </template>
                                                <template v-else>
                                                    <span></span>
                                                </template>
                                            </td>
                                            <td>
                                                <!--割当人数-->
                                                <span class="planService" v-for="(servicesList, servicesIndex) in list.UsedServices" :key="servicesIndex">{{ servicesIndex == 0 ? list.AllocationNum + $t('settings.parts.tableBody4') : '' }}</span>
                                            </td>
                                            <td>
                                                <!--有効割当人数-->
                                                <span class="planService" v-for="(servicesList, servicesIndex) in list.UsedServices" :key="servicesIndex">{{ servicesIndex == 0 ? list.AllocationNumValid + $t('settings.parts.tableBody4') : '' }}</span>
                                            </td>
                                            <!-- サービス・利用件数 -->
                                            <td colspan="2">
                                                <ul v-for="(servicesList, servicesIndex) in list.UsedServices" :key="servicesIndex">
                                                    <li>
                                                        <span>{{ servicesList.ServiceNm }}</span>
                                                    </li>
                                                    <li>
                                                        <span class="planService">
                                                            <i>{{ servicesList.ServiceNm }}</i>
                                                            <b>{{ servicesList.SessionNum + $t('settings.parts.tableBody5') }}</b>
                                                        </span>
                                                    </li>
                                                </ul>
                                            </td>
                                        </template>
                                    </tr>
                                </tbody>
                            </table>
                        </article>
                    </article>
                    <article class="innerTabBox" v-show="activeTab === 'securityTab'">
                        <!--セキュリティ設定-->
                        <section class="form medium">
                            <dl class="dot">
                                <dt>
                                    <!--パスワードポリシー-->
                                    <ul class="formLabel">
                                        <li>
                                            <span>{{ $t('settings.parts.dt10') }}</span>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ commonStore.getConvertDictionary('SecurityPwdPolicy', securityRequest.SecurityPwdPolicy) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="dot">
                                <dt>
                                    <!--パスワード変更催促までの期間-->
                                    <ul class="formLabel">
                                        <li>
                                            <span>{{ $t('settings.parts.dt11') }}</span>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li v-for="securityPwdExpire in securityPwdExpires" :key="securityPwdExpire">
                                            <input type="radio" class="radio" :id="'radio2-' + securityPwdExpire" name="radioGroup2" :value="securityPwdExpire" v-model="securityRequest.SecurityPwdExpire" />
                                            <label class="radio label" :for="'radio2-' + securityPwdExpire">{{ commonStore.getConvertDictionary('SecurityPwdExpire', securityPwdExpire) }}</label>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="dot">
                                <dt>
                                    <!--多要素認証-->
                                    <ul class="formLabel">
                                        <li>
                                            <span>{{ $t('settings.parts.dt12') }}</span>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <!--あり-->
                                            <input type="radio" class="radio" id="radio3-1" name="radioGroup3" value="1" v-model="securityMfaFlag" />
                                            <label class="radio label" for="radio3-1">{{ $t('settings.parts.tableBody1') }}</label>
                                        </li>
                                        <li>
                                            <!--なし-->
                                            <input type="radio" class="radio" id="radio3-2" name="radioGroup3" value="0" v-model="securityMfaFlag" />
                                            <label class="radio label" for="radio3-2">{{ $t('settings.parts.tableBody2') }}</label>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="dot" style="padding-bottom: 10px">
                                <dt>
                                    <!--管理者サイト許可IPアドレス-->
                                    <ul class="formLabel">
                                        <li>
                                            <span>{{ $t('settings.parts.dt13') }}</span>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <textarea style="height: 102px; width: 265px" v-model="securityRequest.SecurityIpList" />
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="dot" style="padding-bottom: 10px">
                                <dt>
                                    <!--メール送信間隔-->
                                    <ul class="formLabel">
                                        <li>
                                            <span>{{ $t('settings.parts.dt14') }}</span>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li style="display: flex; position: relative">
                                            <input type="number" v-model="securityRequest.SecurityMailSpeed" /><span style="align-self: center">{{ $t('settings.parts.tableBody6') }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                        </section>
                        <ul class="btnArea center" style="margin-top: 20px">
                            <li>
                                <button type="button" id="btnNext" class="btnPrimary" @click="securityCreate" :disabled="disabledStore.isDisabled">{{ $t('common.parts.entry') }}</button>
                            </li>
                        </ul>
                    </article>
                    <article class="innerTabBox" v-show="activeTab === 'contactTab'">
                        <!--連絡先-->
                        <span class="" style="padding-left: 10px; position: relative; display: block">{{ $t('settings.message.message3') }}</span>
                        <span class="" style="padding-left: 10px; position: relative; display: block">{{ $t('settings.message.message4') }}</span>
                        <span class="" style="padding-left: 26px; position: relative; display: block">{{ $t('settings.message.message5') }}</span>
                        <article style="height: auto">
                            <table class="table" style="margin-top: 15px; margin-bottom: 15px">
                                <thead>
                                    <tr>
                                        <th style="width: 200px">
                                            <!--危機介入報告等の緊急連絡先-->
                                            <span>{{ $t('settings.parts.tableHeader12') }}</span>
                                        </th>
                                        <th class="width280">
                                            <!--部署-->
                                            <span>{{ $t('settings.parts.tableHeader13') }}</span>
                                        </th>
                                        <th>
                                            <!--氏名-->
                                            <span>{{ $t('settings.parts.tableHeader14') }}</span>
                                        </th>
                                        <th>
                                            <!--電話番号-->
                                            <span>{{ $t('settings.parts.tableHeader15') }}</span>
                                        </th>
                                        <th style="width: auto">
                                            <!--メールアドレス-->
                                            <span>{{ $t('settings.parts.tableHeader16') }}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <th style="display: flex; align-items: center; height: 40px">
                                            <!--日中-->
                                            <span>{{ $t('settings.parts.tableHeader17') }}</span>
                                            <span class="must" style="margin-left: 100px">{{ $t('common.parts.must') }}</span>
                                        </th>
                                        <td>
                                            <input type="text" style="width: 250px" v-model="contactRequest.EmergencyDayDepa" />
                                        </td>
                                        <td>
                                            <input type="text" v-model="contactRequest.EmergencyDayPerson" />
                                        </td>
                                        <td>
                                            <input type="text" v-model="contactRequest.EmergencyDayTel" />
                                        </td>
                                        <td>
                                            <input type="text" style="width: 250px" v-model="contactRequest.EmergencyDayMail" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <th style="display: flex; align-items: center; height: 40px">
                                            <!--夜間-->
                                            <span>{{ $t('settings.parts.tableHeader18') }}</span>
                                            <span class="must" style="margin-left: 100px">{{ $t('common.parts.must') }}</span>
                                        </th>
                                        <td>
                                            <input type="text" style="width: 250px" v-model="contactRequest.EmergencyNightDepa" />
                                        </td>
                                        <td>
                                            <input type="text" v-model="contactRequest.EmergencyNightPerson" />
                                        </td>
                                        <td>
                                            <input type="text" v-model="contactRequest.EmergencyNightTel" />
                                        </td>
                                        <td>
                                            <input type="text" style="width: 250px" v-model="contactRequest.EmergencyNightMail" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <th style="display: flex; align-items: center; height: 40px">
                                            <!--土日祝日-->
                                            <span>{{ $t('settings.parts.tableHeader19') }}</span>
                                            <span class="must" style="margin-left: 72px">{{ $t('common.parts.must') }}</span>
                                        </th>
                                        <td>
                                            <input type="text" style="width: 250px" v-model="contactRequest.EmergencyHolidayDepa" />
                                        </td>
                                        <td>
                                            <input type="text" v-model="contactRequest.EmergencyHolidayPerson" />
                                        </td>
                                        <td>
                                            <input type="text" v-model="contactRequest.EmergencyHolidayTel" />
                                        </td>
                                        <td>
                                            <input type="text" style="width: 250px" v-model="contactRequest.EmergencyHolidayMail" />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <table class="table" style="margin-bottom: 15px">
                                <thead>
                                    <tr>
                                        <th style="width: 200px">
                                            <!--社内相談窓口-->
                                            <span>{{ $t('settings.parts.tableHeader20') }}</span>
                                        </th>
                                        <th class="width280">
                                            <!--部署-->
                                            <span>{{ $t('settings.parts.tableHeader13') }}</span>
                                        </th>
                                        <th>
                                            <!--氏名-->
                                            <span>{{ $t('settings.parts.tableHeader14') }}</span>
                                        </th>
                                        <th>
                                            <!--電話番号-->
                                            <span>{{ $t('settings.parts.tableHeader15') }}</span>
                                        </th>
                                        <th style="width: auto">
                                            <!--メールアドレス-->
                                            <span>{{ $t('settings.parts.tableHeader16') }}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <th>
                                            <!--パワーハラスメント-->
                                            <span>{{ $t('settings.parts.tableHeader21') }}</span>
                                        </th>
                                        <td>
                                            <input type="text" style="width: 250px" v-model="contactRequest.CounterPowerHaraDepa" />
                                        </td>
                                        <td>
                                            <input type="text" v-model="contactRequest.CounterPowerHaraPerson" />
                                        </td>
                                        <td>
                                            <input type="text" v-model="contactRequest.CounterPowerHaraTel" />
                                        </td>
                                        <td>
                                            <input type="text" style="width: 250px" v-model="contactRequest.CounterPowerHaraMail" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>
                                            <!--セクシャルハラスメント-->
                                            <span>{{ $t('settings.parts.tableHeader22') }}</span>
                                        </th>
                                        <td>
                                            <input type="text" style="width: 250px" v-model="contactRequest.CounterSexualHaraDepa" />
                                        </td>
                                        <td>
                                            <input type="text" v-model="contactRequest.CounterSexualHaraPerson" />
                                        </td>
                                        <td>
                                            <input type="text" v-model="contactRequest.CounterSexualHaraTel" />
                                        </td>
                                        <td>
                                            <input type="text" style="width: 250px" v-model="contactRequest.CounterSexualHaraMail" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>
                                            <!--コンプライアンス-->
                                            <span>{{ $t('settings.parts.tableHeader23') }}</span>
                                        </th>
                                        <td>
                                            <input type="text" style="width: 250px" v-model="contactRequest.CounterComplianceDepa" />
                                        </td>
                                        <td>
                                            <input type="text" v-model="contactRequest.CounterCompliancePerson" />
                                        </td>
                                        <td>
                                            <input type="text" v-model="contactRequest.CounterComplianceTel" />
                                        </td>
                                        <td>
                                            <input type="text" style="width: 250px" v-model="contactRequest.CounterComplianceMail" />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </article>
                        <ul class="btnArea center" style="border: none">
                            <li>
                                <button type="button" id="btnNext" class="btnPrimary" @click="contactCreate" :disabled="disabledStore.isDisabled">{{ $t('common.parts.entry') }}</button>
                            </li>
                        </ul>
                    </article>
                    <article class="innerTabBox" v-show="activeTab === 'passwordTab'">
                        <!--企業/家族パスワード-->
                        <section class="form small">
                            <dl class="dot">
                                <dt>
                                    <!--企業ID-->
                                    <ul class="formLabel">
                                        <li>
                                            <span>{{ $t('settings.parts.dt15') }}</span>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ settingStore.settingPassword.CompanyId }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="dot" style="padding-bottom: 8px; align-items: flex-end">
                                <dt>
                                    <!--企業パスワード-->
                                    <ul class="formLabel">
                                        <li>
                                            <span>{{ $t('settings.parts.dt16') }}</span>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <PasswordInput v-model:input="companyPwd" type="password" />
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="dot" style="padding-bottom: 8px; align-items: flex-end">
                                <dt>
                                    <!--家族パスワード-->
                                    <ul class="formLabel">
                                        <li>
                                            <span>{{ $t('settings.parts.dt17') }}</span>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <PasswordInput v-model:input="familyPwd" type="password" :disabledMode="commonStore.companyInfo.TouLinkFlag" />
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="dot" style="margin-bottom: 20px">
                                <dt>
                                    <!--更新日時-->
                                    <ul class="formLabel">
                                        <li>
                                            <span>{{ $t('settings.parts.dt18') }}</span>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ settingStore.settingPassword.PwdUpdDt }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <span style="padding-left: 20px">{{ $t('settings.message.message6') }}</span>
                        </section>
                        <ul class="btnArea center" style="margin-top: 20px">
                            <li>
                                <button type="button" id="btnNext" class="btnPrimary" @click="recommitmentCreate" :disabled="disabledStore.isDisabled">{{ $t('common.parts.entry') }}</button>
                            </li>
                        </ul>
                    </article>
                    <article class="innerTabBox" v-show="activeTab === 'recommitmentTab'">
                        <!--再委託先情報-->
                        <article class="innerTable" style="height: calc(100vh - 294px)">
                            <table class="table noSlelection">
                                <thead>
                                    <tr>
                                        <th style="width: 300px">
                                            <!--名称-->
                                            <span>{{ $t('settings.parts.tableHeader24') }}</span>
                                        </th>
                                        <th style="width: auto">
                                            <!--住所-->
                                            <span>{{ $t('settings.parts.tableHeader25') }}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(list, index) in settingStore.recommitmentList" :key="index">
                                        <td>
                                            <span>{{ list.SubcontractNm }}</span>
                                        </td>
                                        <td>
                                            <span>{{ list.SubcontractAd }}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </article>
                    </article>
                    <article class="innerTabBox" v-show="activeTab === 'logTab'">
                        <!--ログ参照-->
                        <article class="innerTable" style="height: calc(100vh - 328px)">
                            <table class="table noSelection">
                                <thead>
                                    <tr>
                                        <th style="width: 200px">
                                            <!--日付-->
                                            <span>{{ $t('settings.parts.tableHeader26') }}</span>
                                        </th>
                                        <th>
                                            <!--作業者-->
                                            <span>{{ $t('settings.parts.tableHeader27') }}</span>
                                        </th>
                                        <th style="width: auto">
                                            <!--ログ内容-->
                                            <span>{{ $t('settings.parts.tableHeader28') }}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(list, index) in settingStore.logList" :key="index">
                                        <td>
                                            <span>{{ list.LogDt }}</span>
                                        </td>
                                        <td>
                                            <span>{{ list.UserNm }}</span>
                                        </td>
                                        <td>
                                            <span>{{ list.LogMsg }}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </article>
                        <Pagination ref="pagination" :currentPage="currentPage" :totalPages="totalPages" @pageChanged="handlePageChange" />
                    </article>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <button type="button" id="btnBack" class="btnNormal" @click="onBack">{{ $t('common.parts.backPage') }}</button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>

<style scoped>
    .innerTabBox {
        width: 100%;
        height: calc(100% - 100px);
        position: relative;
        display: block;
    }
    .planService > i {
        color: #ffffff;
    }
    .planService > b {
        position: absolute;
        top: calc(50% - 9px);
        left: 0px;
    }
</style>
